import { registerLocaleData } from "@angular/common";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import localeDe from "@angular/common/locales/de";
import localeDeCh from "@angular/common/locales/de-CH";
import localeEnAu from "@angular/common/locales/en-AU";
import localeEnCa from "@angular/common/locales/en-CA";
import localeEnGb from "@angular/common/locales/en-GB";
import localeEs from "@angular/common/locales/es";
import localeFr from "@angular/common/locales/fr";
import localeFrCa from "@angular/common/locales/fr-CA";
import localeFrCh from "@angular/common/locales/fr-CH";
import localeIt from "@angular/common/locales/it";
import localeItCh from "@angular/common/locales/it-CH";
import localeJa from "@angular/common/locales/ja";
import localeAr from "@angular/common/locales/ar";
import localeTr from "@angular/common/locales/tr";
import localePtPT from "@angular/common/locales/pt-PT";
import localePtBR from "@angular/common/locales/pt";
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import { provideCharts, ThemeService } from "ng2-charts";

// Import 3rd party components
import { provideNgxStripe } from "ngx-stripe";
// Import & Configure the Swagger generated API client
// Import routing module
// Import containers
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import { CsvExportModule } from "@ag-grid-community/csv-export";
import { GridChartsModule } from "@ag-grid-enterprise/charts";
import { ClipboardModule } from "@ag-grid-enterprise/clipboard";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { MultiFilterModule } from "@ag-grid-enterprise/multi-filter";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import { SparklinesModule } from "@ag-grid-enterprise/sparklines";
import { StatusBarModule } from "@ag-grid-enterprise/status-bar";
import {
  ActivityService,
  AmazonLoginHandler,
  CurrencyService,
  I18nService,
  ObfuscationService,
  provideM19APIs,
  provideM19TranslocoConfig,
  providePostHog,
  SpStrategyService,
} from "@front/m19-services";
import {
  ArcElement,
  BarController,
  BarElement,
  BubbleController,
  CategoryScale,
  Colors,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PieController,
  PointElement,
  PolarAreaController,
  TimeScale,
  Tooltip,
} from "chart.js";
import Annotation from "chartjs-plugin-annotation";
import { AsyncSubject, forkJoin, Observable } from "rxjs";
import { environment } from "@m19-board/environment";
import { LayoutSelectorService } from "./layout-selector.service";
import { CsvExportService } from "./services/csv-export.service";
import { KeywordTrackerService } from "./services/keyword-tracker.service";
import { ProductTrackerService } from "./services/product-tracker.service";
import { BulkImportService } from "./strategies/strategy-bulk-upload-modal/bulk-import.service";

import { provideRouter, TitleStrategy, withInMemoryScrolling, withRouterConfig } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { DashboardType } from "@front/m19-api-client";
import {
  AMAZON_LOGIN_HANDLER,
  API_BASE_URL,
  DASHBOARD,
  DASHBOARD_TYPE,
  MAX_ASINS_PER_STRATEGY,
  SELLING_PARTNER_DOMAIN,
  StrategyService,
} from "@front/m19-injection-token";
import { AppUpdateService, LAYOUT_SERVICE } from "@front/m19-ui";
import { DASHBOARD_TYPE_360 } from "@m19-board/360-common/360-injection-tokens";
import { Account360Service } from "@m19-board/account360/account360.service";
import { routes, TemplatePageTitleStrategy } from "@m19-board/app.routes";
import { Service360 } from "@m19-board/services/360.service";
import { DashboardConfigService } from "@m19-board/services/dashboard-config.service";
import { Angulartics2Module } from "angulartics2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { provideLottieOptions } from "ngx-lottie";
import { AmazonInvoicesService } from "./settings/amazon-invoices/amazon-invoices.service";
import { MetricDescriptionPipe, MetricTitlePipe } from "@front/m19-utils";

registerLocaleData(localeJa);
registerLocaleData(localeEs);
registerLocaleData(localeDe);
registerLocaleData(localeDeCh);
registerLocaleData(localeIt);
registerLocaleData(localeItCh);
registerLocaleData(localeEnAu);
registerLocaleData(localeEnCa);
registerLocaleData(localeEnGb);
registerLocaleData(localeFrCa);
registerLocaleData(localeFrCh);
registerLocaleData(localeFr);
registerLocaleData(localeAr);
registerLocaleData(localeTr);
registerLocaleData(localePtPT);
registerLocaleData(localePtBR);

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  CsvExportModule,
  MenuModule,
  RangeSelectionModule,
  RowGroupingModule,
  ClipboardModule,
  SetFilterModule,
  MultiFilterModule,
  StatusBarModule,
  SparklinesModule,
  GridChartsModule,
]);

const CLIENT_ID = "amzn1.application-oa2-client.de308868b4984b2b8ceac09afed2b2dd";
declare const amazon: any;

export function loadAmazonScript(): Observable<AmazonLoginHandler> {
  const amazonHandlerProvider = new AsyncSubject<AmazonLoginHandler>();
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = "https://assets.loginwithamazon.com/sdk/na/login1.js";
  script.onload = function () {
    amazon.Login.setClientId(CLIENT_ID);
    // eslint-disable-next-line no-console
    console.info("Amazon script loaded");
    amazonHandlerProvider.next(amazon.Login);
    amazonHandlerProvider.complete();
  };
  document.getElementsByTagName("head")[0].appendChild(script);
  return amazonHandlerProvider;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      inject(AppUpdateService);
      const i18nService = inject(I18nService);
      const currencyService = inject(CurrencyService);

      return forkJoin([i18nService.initLanguage(), currencyService.initCurrencies()]);
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" }),
      withRouterConfig({ defaultQueryParamsHandling: "merge" }),
    ),
    {
      provide: API_BASE_URL,
      useValue: environment.apiEndPoint,
    },
    {
      provide: SELLING_PARTNER_DOMAIN,
      useValue: environment.spOauthRedirectDomain,
    },
    {
      provide: AMAZON_LOGIN_HANDLER,
      useValue: loadAmazonScript(),
    },
    {
      provide: DASHBOARD_TYPE,
      useValue: "dashboard",
    },
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
    },
    {
      provide: LAYOUT_SERVICE,
      useExisting: LayoutSelectorService,
    },
    {
      provide: StrategyService,
      useExisting: SpStrategyService,
    },
    {
      provide: MAX_ASINS_PER_STRATEGY,
      useValue: 10_000,
    },
    {
      provide: DASHBOARD,
      useValue: "board",
    },
    { provide: DASHBOARD_TYPE_360, useValue: DashboardType.ACCOUNT_MARKETPLACE },
    DashboardConfigService,
    { provide: Service360, useExisting: Account360Service },
    importProvidersFrom(Angulartics2Module.forRoot()),
    provideServiceWorker("ngsw-worker.js"),
    providePostHog(environment.posthogKey),
    provideM19APIs(),
    provideM19TranslocoConfig(environment.production),
    provideNgxStripe(),
    provideLottieOptions({
      player: () => import("lottie-web"),
    }),
    ThemeService,
    LayoutSelectorService,
    AppUpdateService,
    CsvExportService,
    KeywordTrackerService,
    ProductTrackerService,
    ActivityService,
    BulkImportService,
    ObfuscationService,
    I18nService,
    AmazonInvoicesService,
    MetricTitlePipe,
    MetricDescriptionPipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideCharts({
      registerables: [
        BarController,
        Legend,
        ChartDataLabels,
        Annotation,
        LineController,
        DoughnutController,
        PolarAreaController,
        PieController,
        BubbleController,
        Colors,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        BarElement,
        ArcElement,
        Tooltip,
        TimeScale,
        {
          id: "nodataplugin",
          beforeDraw: (chart) => {
            const { ctx } = chart;
            ctx.save();
            ctx.globalCompositeOperation = "destination-over";
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          },
          afterDraw: function (chart) {
            if (chart.data.datasets.length === 0) {
              // No data is present
              const ctx = chart.ctx;
              const width = chart.width;
              const height = chart.height;
              chart.clear();

              ctx.save();
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.font = "16px normal 'Helvetica Nueue'";
              ctx.fillText("No data to display", width / 2, height / 2);
              ctx.restore();
            }
          },
        },
      ],
    }),
  ],
};
